// replacement for https://github.com/jackmoore/zoom
const ZOOM_FADE_MS = 120;
/**
 * Attaches a native mouseover/touch image zoom to `source`. The full-res image
 * URL is read from `data-src`, `currentSrc`, or `src` of the first `<img>`
 * descendant — matching the lookup order of the former jquery-zoom plugin.
 *
 * On desktop the zoom triggers on `mouseenter`/`mousemove`/`mouseleave`.
 * On touch devices a single tap toggles the zoom on/off; a second tap (or
 * dragging while active) pans the zoomed image.
 *
 * Returns a cleanup function that removes all listeners, removes the injected
 * zoom image, and restores any inline styles that were overwritten. Safe to
 * call at any time, even before the zoom image has finished loading.
 *
 * @example
 * const cleanup = applyZoom(slideElement);
 * // later, e.g. in disconnectedCallback:
 * cleanup();
 */
export function applyZoom(source) {
    const srcImg = source.querySelector('img');
    if (!srcImg)
        return () => { };
    const url = srcImg.getAttribute('data-src') || srcImg.currentSrc || srcImg.src;
    if (!url)
        return () => { };
    const zoomImg = document.createElement('img');
    zoomImg.setAttribute('role', 'presentation');
    zoomImg.alt = '';
    zoomImg.className = 'zoomImg';
    const savedPosition = source.style.position;
    const savedOverflow = source.style.overflow;
    // Populated inside onload once listeners are attached; called by the returned
    // cleanup function whether or not onload has fired yet.
    let removeListeners = () => { };
    zoomImg.onload = () => {
        const computedPos = getComputedStyle(source).position;
        source.style.position = /(absolute|fixed)/.test(computedPos) ? computedPos : 'relative';
        source.style.overflow = 'hidden';
        Object.assign(zoomImg.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            opacity: '0',
            width: `${zoomImg.naturalWidth}px`,
            height: `${zoomImg.naturalHeight}px`,
            border: 'none',
            maxWidth: 'none',
            maxHeight: 'none',
            transition: `opacity ${ZOOM_FADE_MS}ms`,
            pointerEvents: 'none',
        });
        source.appendChild(zoomImg);
        let srcW, srcH, xRatio, yRatio;
        let docLeft, docTop;
        function snapDimensions() {
            srcW = source.offsetWidth;
            srcH = source.offsetHeight;
            xRatio = (zoomImg.naturalWidth - srcW) / srcW;
            yRatio = (zoomImg.naturalHeight - srcH) / srcH;
            const rect = source.getBoundingClientRect();
            docLeft = rect.left + window.scrollX;
            docTop = rect.top + window.scrollY;
        }
        function moveTo(pageX, pageY) {
            const left = Math.max(Math.min(pageX - docLeft, srcW), 0);
            const top = Math.max(Math.min(pageY - docTop, srcH), 0);
            zoomImg.style.left = `${left * -xRatio}px`;
            zoomImg.style.top = `${top * -yRatio}px`;
        }
        function show(pageX, pageY) {
            snapDimensions();
            moveTo(pageX, pageY);
            zoomImg.style.opacity = '1';
        }
        function hide() {
            zoomImg.style.opacity = '0';
        }
        let touched = false;
        const onMouseEnter = (e) => show(e.pageX, e.pageY);
        const onMouseLeave = () => hide();
        const onMouseMove = (e) => moveTo(e.pageX, e.pageY);
        const onTouchStart = (e) => {
            e.preventDefault();
            const t = e.touches[0] ?? e.changedTouches[0];
            if (touched) {
                touched = false;
                hide();
            }
            else {
                touched = true;
                show(t.pageX, t.pageY);
            }
        };
        const onTouchMove = (e) => {
            e.preventDefault();
            const t = e.touches[0] ?? e.changedTouches[0];
            moveTo(t.pageX, t.pageY);
        };
        const onTouchEnd = (e) => {
            e.preventDefault();
            if (touched) {
                touched = false;
                hide();
            }
        };
        source.addEventListener('mouseenter', onMouseEnter);
        source.addEventListener('mouseleave', onMouseLeave);
        source.addEventListener('mousemove', onMouseMove);
        source.addEventListener('touchstart', onTouchStart, { passive: false });
        source.addEventListener('touchmove', onTouchMove, { passive: false });
        source.addEventListener('touchend', onTouchEnd, { passive: false });
        removeListeners = () => {
            source.removeEventListener('mouseenter', onMouseEnter);
            source.removeEventListener('mouseleave', onMouseLeave);
            source.removeEventListener('mousemove', onMouseMove);
            source.removeEventListener('touchstart', onTouchStart);
            source.removeEventListener('touchmove', onTouchMove);
            source.removeEventListener('touchend', onTouchEnd);
        };
        // Pre-snapshot dimensions so the first mousemove doesn't need to wait for
        // an explicit mouseenter, matching the original plugin's init-on-load call.
        snapDimensions();
    };
    zoomImg.src = url;
    return () => {
        zoomImg.onload = null;
        removeListeners();
        zoomImg.remove();
        source.style.position = savedPosition;
        source.style.overflow = savedOverflow;
    };
}
